import { createSlice, PayloadAction } from "@reduxjs/toolkit";
// Models
import IProperty from "models/Property";
import IPropertyOwnership from "models/PropertyOwnership";
import { IPropertyOwnershipsGroup } from "models/PropertyOwnershipsGroup";
// Types
import PropertyOwnershipStatus from "types/PropertyOwnershipStatus";
// Async
import PropertyOwnershipsAsync from "./propertyOwnershipsAsync";

interface IState {
  propertyOwnerships: IPropertyOwnership[] | null;
  total: number;
  myPropertyOwnershipsTotal: number;
  myPropertyOwnerships: IPropertyOwnership[] | null;
  propertyOwnershipsGroups: IPropertyOwnershipsGroup | null;
  propertyOwnershipDetails: IPropertyOwnership | null;
  formData: {
    property: IProperty | null,
  };
  params: any;
  myPropertyOwnershipsParams: {
    statuses: PropertyOwnershipStatus[],
  };
}

const initialState:IState = {
  propertyOwnerships: null,
  total: 0,
  myPropertyOwnershipsTotal: 0,
  myPropertyOwnerships: null,
  propertyOwnershipsGroups: null,
  propertyOwnershipDetails: null,
  formData: {
    property: null,
  },
  params: {},
  myPropertyOwnershipsParams: {
    statuses: [PropertyOwnershipStatus.Available],
  },
};

const propertyOwnershipsSlice = createSlice({
  name: 'propertyOwnerships',
  initialState,
  reducers: {
    setPropertyOwnershipDetails: (state, action:PayloadAction<IPropertyOwnership | null>) => {
      state.propertyOwnershipDetails = action.payload;
    },
    setFormData: (state, action:PayloadAction<any>) => {
      state.formData.property = action.payload;
    },
    setParams: (state, action:PayloadAction<any>) => {
      state.params = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch property ownerships (search)
      .addCase(PropertyOwnershipsAsync.fetchPropertyOwnerships.pending, (state) => {
        state.propertyOwnerships = null;
      })
      .addCase(PropertyOwnershipsAsync.fetchPropertyOwnerships.fulfilled, (state, action:PayloadAction<any>) => {
        state.propertyOwnerships = action.payload.data;
        state.total = action.payload.total;
      })
      // Fetch property ownership (by id)
      .addCase(PropertyOwnershipsAsync.fetchPropertyOwnership.pending, (state) => {
        state.propertyOwnershipDetails = null;
      })
      .addCase(PropertyOwnershipsAsync.fetchPropertyOwnership.fulfilled, (state, action:PayloadAction<IPropertyOwnership>) => {
        state.propertyOwnershipDetails = action.payload;
      })
      // Fetch my property ownerships
      .addCase(PropertyOwnershipsAsync.fetchMyPropertyOwnerships.pending, (state, action:any) => {
        state.myPropertyOwnershipsParams = action.meta.arg
        state.myPropertyOwnerships = null;
        state.myPropertyOwnershipsTotal = 0;
      })
      .addCase(PropertyOwnershipsAsync.fetchMyPropertyOwnerships.fulfilled, (state, action:PayloadAction<any>) => {
        state.myPropertyOwnerships = action.payload.data;
        state.myPropertyOwnershipsTotal = action.payload.total;
      })
      // Fetch property ownerships groups
      .addCase(PropertyOwnershipsAsync.fetchPropertyOwnershipsGroups.pending, (state) => {
        state.propertyOwnershipsGroups = null;
      })
      .addCase(PropertyOwnershipsAsync.fetchPropertyOwnershipsGroups.fulfilled, (state, action:PayloadAction<any>) => {
        state.propertyOwnershipsGroups = action.payload;
      })
      // Create property ownership
      .addCase(PropertyOwnershipsAsync.createPropertyOwnerships.fulfilled, (state, action:PayloadAction<any>) => {
        state.myPropertyOwnerships = state.myPropertyOwnerships
          ? state.myPropertyOwnershipsParams.statuses.includes(PropertyOwnershipStatus.Available) ? [action.payload, ...state.myPropertyOwnerships] : state.myPropertyOwnerships
          : state.myPropertyOwnershipsParams.statuses.includes(PropertyOwnershipStatus.Available) ? [action.payload] : [];
        state.myPropertyOwnershipsTotal = state.myPropertyOwnershipsTotal + 1;
      })
      // Delete property ownership
      .addCase(PropertyOwnershipsAsync.deletePropertyOwnerships.fulfilled, (state, action:any) => {
        const { arg:propertyOwnershipId } = action.meta;
        state.myPropertyOwnerships = state.myPropertyOwnerships
          ? state.myPropertyOwnerships.filter((propertyOwnership: IPropertyOwnership) => propertyOwnership.id !== propertyOwnershipId)
          : [];
        state.myPropertyOwnershipsTotal = state.myPropertyOwnershipsTotal - 1;
      })
      // Update property ownership
      .addCase(PropertyOwnershipsAsync.updatePropertyOwnerships.fulfilled, (state, action:PayloadAction<any>) => {
        state.myPropertyOwnerships = state.myPropertyOwnerships
          ? state.myPropertyOwnerships.map((propertyOwnership: IPropertyOwnership) => {
            if (propertyOwnership.id === action.payload.id) {
              return action.payload;
            } else {
              return propertyOwnership;
            }
          }) : [];
      })
  }
});

export const PropertyOwnershipsActions = propertyOwnershipsSlice.actions;

export default propertyOwnershipsSlice.reducer;
